import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Split, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { DatePicker } from '../ui/date-picker';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { splitFulfilment } from '../../lib/api';
import { fmtQty } from './phasedUtils';

const initRows = (lines, prefill) => lines.map((ln, i) => {
  const ordered = Number(ln.quantity) || 0;
  const pre = prefill?.[i];
  const short = pre != null && pre < ordered;
  return { ticked: short, ship: String(short ? pre : ordered), closeShort: false };
});

const planFor = (lines, rows) => lines.map((ln, i) => {
  const ordered = Number(ln.quantity) || 0;
  const r = rows[i] || {};
  const ship = r.ticked ? Math.min(Math.max(Number(r.ship) || 0, 0), ordered) : ordered;
  const rest = Math.round((ordered - ship) * 1000) / 1000;
  return { index: i, line_id: ln.id || null, ship_qty: ship, keep_open_qty: r.closeShort ? 0 : rest, rest };
});

const SplitRow = ({ line, row, plan, onChange, index }) => (
  <tr className={`border-b last:border-0 ${row.ticked ? 'bg-primary/[0.04]' : ''}`} data-testid={`split-row-${index}`}>
    <td className="py-2 pl-3 pr-2 align-top">
      <Checkbox checked={row.ticked} onCheckedChange={(v) => onChange({ ticked: !!v })} data-testid={`split-tick-${index}`} />
    </td>
    <td className="py-2 pr-2 align-top">
      <div className="text-sm font-medium">{line.product_name}</div>
      {line.supplier_product_code && <div className="font-mono text-[11px] text-muted-foreground">{line.supplier_product_code}</div>}
    </td>
    <td className="py-2 pr-2 text-right align-top font-mono text-sm">{fmtQty(line.quantity)}</td>
    <td className="py-2 pr-2 align-top">
      <Input type="number" min="0" step="any" value={row.ship} disabled={!row.ticked} onChange={(e) => onChange({ ship: e.target.value })}
        className="h-8 w-24 text-right font-mono" data-testid={`split-ship-${index}`} />
    </td>
    <td className="py-2 pr-2 text-right align-top font-mono text-sm" data-testid={`split-rest-${index}`}>{row.ticked ? fmtQty(plan.rest) : '—'}</td>
    <td className="py-2 pr-3 align-top">
      {row.ticked && plan.rest > 0 ? (
        <label className="flex items-center gap-1.5 text-xs">
          <Checkbox checked={row.closeShort} onCheckedChange={(v) => onChange({ closeShort: !!v })} data-testid={`split-close-short-${index}`} />
          Close short
        </label>
      ) : <span className="text-xs text-muted-foreground">—</span>}
    </td>
  </tr>
);

const sum = (arr, key) => arr.reduce((s, p) => s + (Number(p[key]) || 0), 0);

export const SplitFulfilmentDialog = ({ open, onOpenChange, dispatch, prefill, onDone }) => {
  const lines = useMemo(() => dispatch?.lines || [], [dispatch]);
  const [rows, setRows] = useState([]);
  const [balanceDate, setBalanceDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setRows(initRows(lines, prefill)); setBalanceDate(''); }
  }, [open, lines, prefill]);

  const plan = useMemo(() => planFor(lines, rows), [lines, rows]);
  const sending = sum(plan, 'ship_qty');
  const keeping = sum(plan, 'keep_open_qty');
  const closing = sum(plan, 'rest') - keeping;
  const ready = rows.some((r) => r.ticked) && keeping > 0 && sending > 0 && !!balanceDate;
  const setRow = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await splitFulfilment(dispatch.id, { lines: plan.map(({ rest, ...p }) => p), balance_date: balanceDate });
      onDone(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not split this order — check the quantities and date');
    } finally {
      setSaving(false);
    }
  };

  if (!dispatch) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="split-fulfilment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Split className="h-5 w-5 text-primary" /> Split Fulfilment</DialogTitle>
          <DialogDescription>
            Order #{dispatch.dispatch_number} · PO {dispatch.po_number || '—'} · {dispatch.customer_name}. Tick the products that need
            more dispatches and enter what goes on this delivery. The rest moves to a new linked order under the same PO.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[45vh] overflow-y-auto rounded-lg border">
          <table className="w-full">
            <thead className="sticky top-0 bg-muted/70 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-8 py-2 pl-3" />
                <th className="py-2 pr-2 text-left font-medium">Product</th>
                <th className="py-2 pr-2 text-right font-medium">Ordered</th>
                <th className="py-2 pr-2 text-left font-medium">Send now</th>
                <th className="py-2 pr-2 text-right font-medium">Rest</th>
                <th className="py-2 pr-3 text-left font-medium">Rest goes to</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => rows[i] && (
                <SplitRow key={ln.id || i} index={i} line={ln} row={rows[i]} plan={plan[i]} onChange={(p) => setRow(i, p)} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid gap-4 sm:grid-cols-[220px_1fr] sm:items-end">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs"><CalendarClock className="h-3.5 w-3.5" /> Next delivery date *</Label>
            <DatePicker value={balanceDate} onChange={setBalanceDate} placeholder="Pick the next delivery" data-testid="split-balance-date" />
          </div>
          <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs leading-relaxed" data-testid="split-summary">
            <div>This delivery: <b className="font-mono">{fmtQty(sending)}</b> units</div>
            <div>New linked order: <b className="font-mono">{fmtQty(keeping)}</b> units{balanceDate ? ` on ${balanceDate}` : ''}</div>
            {closing > 0 && <div className="text-amber-700">Closed short (not sent): <b className="font-mono">{fmtQty(closing)}</b> units</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="split-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!ready || saving} data-testid="split-confirm">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Split className="mr-2 h-4 w-4" />}
            Split Fulfilment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
